import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { connectDrive, useDisconnectDrive, useDriveStatus, type DriveStatus } from "@/api/drive";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Button, buttonClass } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDate } from "@/lib/format";
import { openOutside } from "@/lib/platform";

/** The company's one Google Drive connection, which every director's uploads go through. */
export function DrivePanel() {
  const drive = useDriveStatus();
  const disconnect = useDisconnectDrive();
  const [confirming, setConfirming] = useState(false);
  const [connectError, setConnectError] = useState<unknown>(null);
  const { refetch } = drive;

  // Google's consent screen is finished in another window; read the status again on return.
  useEffect(() => {
    const onFocus = (): void => void refetch();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refetch]);

  function connect(): void {
    setConnectError(null);
    openOutside(() => connectDrive().then((answer) => answer.auth_url)).catch(setConnectError);
  }

  function connectButtons(label: string) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={connect}>
          {label}
        </Button>
        <Button size="sm" variant="outline" onClick={() => void refetch()}>
          Check again
        </Button>
      </div>
    );
  }

  function body(status: DriveStatus) {
    if (!status.set_up) {
      return (
        <p className="text-sm">
          Google Drive is not set up on this server. See deploy/README.md → Google Drive.
        </p>
      );
    }
    if (status.status === "needs_reconnect") {
      return (
        <>
          <Alert variant="error">
            Google Drive needs reconnecting. Uploads are paused until a director reconnects.
          </Alert>
          {connectButtons("Reconnect")}
        </>
      );
    }
    if (status.status === "not_connected") {
      return (
        <>
          <p className="text-sm">
            Sign in with the company Google account (the one that owns the company Drive).
          </p>
          {connectButtons("Connect Google Drive")}
        </>
      );
    }
    return (
      <>
        <p className="text-sm">
          Connected as <strong>{status.account_email}</strong> by {status.connected_by} on{" "}
          {formatDate(status.connected_at)}
        </p>
        <p className="text-xs text-muted-foreground">
          After the first upload, move the PBO Invoicing folder into PBO Company Drive / 04
          Finance &amp; Accounts. The app keeps access to it wherever it is moved.
        </p>
        <RequestError error={disconnect.error} />
        {confirming ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Disconnect? Files already in Drive stay there.</span>
            <Button
              size="sm"
              variant="destructive"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate(undefined, { onSuccess: () => setConfirming(false) })}
            >
              Confirm
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {status.folder_url && (
              <a
                href={status.folder_url}
                target="_blank"
                rel="noreferrer"
                className={buttonClass("outline", "sm")}
              >
                <ExternalLink className="h-4 w-4" aria-hidden />
                Open folder
              </a>
            )}
            <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
              Disconnect
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Bills and payment proofs you upload are filed in the company Google Drive under PBO
        Invoicing / financial year / Bills or Payment proofs.
      </p>
      {drive.isPending ? (
        <p className="text-muted-foreground">Loading the Drive connection…</p>
      ) : drive.isError ? (
        <RequestError error={drive.error} />
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-3">
            <RequestError error={connectError} />
            {body(drive.data)}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
